import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SupportedLanguage, DEFAULT_LANGUAGE } from "@/constants/languages";
import { LoginRoutes } from "@/routes/config";
import {
  useLanguageQuery,
  useLanguageMutation,
  useStudySettingsQuery,
  useStudySettingsMutation,
} from "@/infrastructure/adapters/user-preference/user-preference.query";
import { UpdateStudySettingsPayload } from "@/infrastructure/adapters/user-preference/user-preference.types";

import { toast } from "@/utils/toast";
import { clearStoredLanguage, readStoredLanguage, saveStoredLanguage } from "@/utils/language-storage";
import i18nInstance from "@/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";

/** Check if current route is a public/login route (no auth) */
const isPublicRoute = () => LoginRoutes.some((route) => window.location.pathname.startsWith(route));

/**
 * Hook for user language preference management
 *
 * @returns {Object} Language state and setter
 * - currentLanguage: Current active language
 * - setLanguage: Function to change language (with i18n sync)
 * - isLoading: Initial loading state
 * - isUpdating: Mutation in progress
 * - error: Any error from query/mutation
 *
 * @example
 * ```tsx
 * const { currentLanguage, setLanguage, isLoading } = useUserLanguage();
 *
 * const handleChange = (lang: SupportedLanguage) => {
 *   setLanguage(lang);
 * };
 * ```
 */
export const useUserLanguage = () => {
  const { i18n } = useTranslation();
  const query = useLanguageQuery();
  const mutation = useLanguageMutation();

  // Use API data when available, otherwise preserve i18n's current language (from localStorage)
  const currentLanguage = query.data?.language ?? (i18n.language as SupportedLanguage) ?? DEFAULT_LANGUAGE;

  const setLanguage = useCallback(
    async (language: SupportedLanguage) => {
      const previousLanguage = currentLanguage;
      const previousStoredLanguage = readStoredLanguage();

      try {
        await i18n.changeLanguage(language);

        // Public routes (login, register): no auth token, so no API call — and the choice stays in
        // memory only. It is persisted by the login flow once submit succeeds (see LoginForm)
        if (isPublicRoute()) return;

        // Only explicit-choice path, so the only place that persists (i18next caching is off)
        saveStoredLanguage(language);

        await mutation.mutateAsync(language);
      } catch (error) {
        await i18n.changeLanguage(previousLanguage);

        // Restore exact previous state — writing previousLanguage back would persist an
        // OS-derived default
        if (previousStoredLanguage) {
          // Cast: written back verbatim, so a legacy value survives untouched
          saveStoredLanguage(previousStoredLanguage as SupportedLanguage);
        } else {
          clearStoredLanguage();
        }

        toast.error(i18nInstance.t("language.changeFailed"));
      }
    },
    [i18n, mutation, currentLanguage],
  );

  return {
    currentLanguage,
    setLanguage,
    isLoading: query.isLoading,
    isUpdating: mutation.isPending,
    error: query.error || mutation.error,
  };
};

/**
 * Hook for study settings management
 */
export const useStudySettings = () => {
  const queryClient = useQueryClient();
  const query = useStudySettingsQuery();
  const mutation = useStudySettingsMutation();

  const settings = query.data;

  const updateSettings = useCallback(
    async (newSettings: UpdateStudySettingsPayload) => {
      try {
        await mutation.mutateAsync(newSettings);
        // Reset workbook vocabulary queries only if the furigana visibility setting was part of the update
        if (Object.prototype.hasOwnProperty.call(newSettings, "isFuriganaEnabled")) {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VOCABULARY.WORDS, i18nInstance.language] });
        }
        // Clear stale recommendation cache when toggling on — the optimistic update
        // flips isRecommendationEnabled in cache, which re-enables useRecommendationQuery
        // and triggers a single fresh fetch automatically.
        if (newSettings.isRecommendationEnabled === true) {
          queryClient.resetQueries({ queryKey: [QUERY_KEYS.STUDY.RECOMMENDATION] });
        }

        if (newSettings.weeklyStudyTimeTarget) {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STUDY_TIME.WEEKLY] });
        }
        return true;
      } catch (error) {
        toast.error(i18nInstance.t("common.updateFailed"));
        return false;
      }
    },
    [mutation, queryClient],
  );

  return {
    settings,
    updateSettings,
    isLoading: query.isLoading,
    isUpdating: mutation.isPending,
    error: query.error || mutation.error,
  };
};
